import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { fetchPlaybackSource } from "@/services/api/player";
import { apiUrl } from "@/services/http/api-url";

/**
 * S3 F-8: how long the video may wait for data (`waiting` / `stalled`, no `playing` since) before
 * the URL is refreshed as if it had errored. Safari may stall on a refused range request instead
 * of firing `error`.
 */
export const LOCAL_STALL_REFRESH_MS = 15_000;

/**
 * C1 (LRN-19): the `<video src>` for a server-hosted lesson. Asks the API (Bearer, entitlement
 * checked) for a short-lived, user+lesson-scoped URL and resolves it against the API base, so
 * it works with the API on another origin. `null` until loaded or when not applicable.
 *
 * R3-L9: the token lives ~2 h. After a long pause the browser's next range request is refused
 * and the `<video>` errors; the hook catches that error first (a capture listener on the
 * document — media errors don't bubble, but capture still sees them), fetches a fresh URL,
 * swaps it in and puts the student back at the same time. The player's own `onError` doesn't
 * fire for that one. If the fresh URL fails too before it ever loaded, the error goes through.
 * S3 F-8: a video that waits for data for LOCAL_STALL_REFRESH_MS without recovering is refreshed
 * the same way (Safari can stall on the refused request instead of erroring).
 */
export function useLocalPlaybackUrl(
  lessonId: string,
  enabled: boolean,
  onError?: (err?: unknown) => void,
): string | null {
  // Tagged with its lesson, so the render right after a lesson change never hands the new
  // lesson's (keyed) <video> the previous lesson's URL (LRN-10 seeks after its metadata loads).
  const [source, setSource] = useState<{ lessonId: string; url: string } | null>(null);
  const url = source && source.lessonId === lessonId ? source.url : null;
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });
  /** Set while a refreshed URL hasn't loaded yet: a second error then is a real one. */
  const refreshing = useRef(false);
  /** Where to put the student back once the refreshed URL has loaded. */
  const resumeAt = useRef<number | null>(null);

  useEffect(() => {
    setSource(null);
    refreshing.current = false;
    resumeAt.current = null;
    if (!enabled) return;
    let cancelled = false;
    fetchPlaybackSource(lessonId)
      .then((source) => {
        if (cancelled) return;
        if (source?.kind === "local") setSource({ lessonId, url: apiUrl(source.url) });
        else onErrorRef.current?.();
      })
      .catch((err: unknown) => {
        if (!cancelled) onErrorRef.current?.(err);
      });
    return () => {
      cancelled = true;
    };
  }, [lessonId, enabled]);

  // A layout effect: the capture listeners are in place in the same commit that puts the new
  // `src` on the <video>, so no error from it can reach the player's own onError first.
  useLayoutEffect(() => {
    if (!url) return;
    let cancelled = false;
    const ours = (event: Event): HTMLMediaElement | null => {
      const target = event.target;
      return target instanceof HTMLMediaElement && target.getAttribute("src") === url
        ? target
        : null;
    };

    const refresh = (media: HTMLMediaElement) => {
      refreshing.current = true;
      resumeAt.current = media.currentTime > 0 ? media.currentTime : null;
      fetchPlaybackSource(lessonId)
        .then((source) => {
          if (cancelled) return;
          if (source?.kind === "local") setSource({ lessonId, url: apiUrl(source.url) });
          else onErrorRef.current?.();
        })
        .catch((err: unknown) => {
          if (!cancelled) onErrorRef.current?.(err);
        });
    };

    const onMediaError = (event: Event) => {
      const media = ours(event);
      if (!media || refreshing.current) return;
      event.stopImmediatePropagation();
      clearStall();
      refresh(media);
    };

    let stallTimer: ReturnType<typeof setTimeout> | null = null;
    const clearStall = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = null;
    };
    const onStall = (event: Event) => {
      const media = ours(event);
      if (!media || refreshing.current || stallTimer) return;
      stallTimer = setTimeout(() => {
        stallTimer = null;
        // HAVE_FUTURE_DATA (3): it recovered without a `playing` event reaching us.
        if (cancelled || refreshing.current || media.readyState >= 3) return;
        refresh(media);
      }, LOCAL_STALL_REFRESH_MS);
    };
    const onProgressing = (event: Event) => {
      if (ours(event)) clearStall();
    };

    const onLoadedMetadata = (event: Event) => {
      const media = ours(event);
      if (!media || resumeAt.current == null) return;
      media.currentTime = resumeAt.current;
      resumeAt.current = null;
      try {
        void Promise.resolve(media.play()).catch(() => undefined);
      } catch {
        // play() can be refused (autoplay policy); the student presses play.
      }
    };

    const onLoadedData = (event: Event) => {
      if (ours(event)) refreshing.current = false;
    };

    const listeners: [string, (event: Event) => void][] = [
      ["error", onMediaError],
      ["loadedmetadata", onLoadedMetadata],
      ["loadeddata", onLoadedData],
      ["waiting", onStall],
      ["stalled", onStall],
      ["playing", onProgressing],
      ["canplay", onProgressing],
    ];
    for (const [name, listener] of listeners) document.addEventListener(name, listener, true);
    return () => {
      cancelled = true;
      clearStall();
      for (const [name, listener] of listeners) {
        document.removeEventListener(name, listener, true);
      }
    };
  }, [url, lessonId]);

  return url;
}
